/**
 * Ordering maths for manually re-ordered kanban cards.
 *
 * Kept out of the component so the rules are testable on their own: the
 * "picked a card up and put it back down, it jumped to the bottom" bug came
 * from the drag wiring rather than from this function, but the two are easy to
 * confuse and the semantics below are the part most likely to be edited later.
 */

/** Where the drop landed. `null` target means empty column space. */
export interface DropTarget {
  /** Card the pointer was over, or null when over empty column space. */
  targetId: number | null;
  /** Insert after the target rather than before it. */
  after: boolean;
}

/**
 * New id order for a stage after `movedId` is dropped on it.
 *
 * Returns `null` when nothing should change — the caller uses that to skip the
 * network round trip entirely. Three cases produce no change:
 *   - the card was dropped on itself
 *   - the target isn't in this stage
 *   - the resulting order is identical to the current one
 */
export function orderAfterDrop(
  currentIds: number[],
  movedId: number,
  { targetId, after }: DropTarget,
): number[] | null {
  // Dropping a card on itself is a no-op, never a move to the end.
  if (targetId === movedId) return null;

  const ids = currentIds.filter(id => id !== movedId);

  if (targetId === null) {
    // Empty column space below the cards — send it to the end.
    ids.push(movedId);
  } else {
    const at = ids.indexOf(targetId);
    if (at === -1) return null;
    ids.splice(after ? at + 1 : at, 0, movedId);
  }

  const unchanged =
    currentIds.length === ids.length && currentIds.every((id, i) => id === ids[i]);
  return unchanged ? null : ids;
}
